import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

class SuitSet extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || 'NZD';
    this.mainProductId = this.dataset.mainProductId;
    this.defaultAddToCartText = this.dataset.addToCartText || 'Add to cart';
    this.selections = {};
    
    this.totalPriceEl = this.querySelector('[data-total-price]');
    this.statusEl = this.querySelector('[data-selected-status]');
    this.totalRowEl = this.querySelector('.suit-set-footer__total-row');
    
    this.items = Array.from(this.querySelectorAll('.suit-set-item'));
    
    this.items.forEach(itemEl => {
      const productId = itemEl.dataset.productId;
      const trigger = itemEl.querySelector('[data-action="open-size-drawer"]');
      const dialog = itemEl.querySelector('dialog.size-drawer');
      const closeBtn = itemEl.querySelector('[data-action="close-size-drawer"]');
      
      const variants = JSON.parse(itemEl.querySelector('.product-variants-json').textContent);
      const options = JSON.parse(itemEl.querySelector('.product-options-json').textContent);
      
      const sizeOpt = options.find(opt => opt.name.toLowerCase() === 'size') || options[0];
      const lengthOpt = options.find(opt => opt.name.toLowerCase() === 'length');
      
      const sizeIndex = sizeOpt ? sizeOpt.position - 1 : 0;
      const lengthIndex = lengthOpt ? lengthOpt.position - 1 : -1;
      
      let selectedLength = null;
      let selectedSize = null;
      let lastSelectedLength = null;
      
      const lengthBtns = itemEl.querySelectorAll('.length-btn');
      const sizeBtns = itemEl.querySelectorAll('.size-btn');
      const submitBtn = itemEl.querySelector('.select-size-submit-btn');
      
      const closeDrawer = () => {
        dialog.classList.add('sd-closing');
        const finish = () => {
          dialog.classList.remove('sd-closing');
          dialog.removeEventListener('animationend', finish);
          dialog.close();
          document.documentElement.classList.remove('sd-scroll-lock');
        };
        dialog.addEventListener('animationend', finish);
      };
      
      const getLengthTypeForSize = (sizeValue) => {
        if (!sizeValue || sizeValue.length < 2) return 'regular';
        const lastChar = sizeValue.slice(-1).toLowerCase();
        if (sizeValue.toLowerCase() === 'xs' || sizeValue.toLowerCase() === 's') {
          return 'regular';
        }
        if (lastChar === 's') return 'short';
        if (lastChar === 't') return 'tall';
        if (lastChar === 'r') return 'regular';
        return 'regular';
      };

      const openDrawer = () => {
        const selection = this.selections[productId];
        
        // Find existing length types for this product
        const existingLengths = new Set();
        variants.forEach(v => {
          existingLengths.add(getLengthTypeForSize(v.options[sizeIndex]));
        });

        if (selection) {
          selectedSize = selection.size;
          selectedLength = selection.length;
          lastSelectedLength = selection.length;
        } else {
          selectedSize = null;
          if (lengthIndex === -1 && lengthBtns.length > 0) {
            if (existingLengths.has('regular')) {
              selectedLength = 'regular';
            } else if (existingLengths.size > 0) {
              selectedLength = Array.from(existingLengths)[0];
            } else {
              selectedLength = 'regular';
            }
            lastSelectedLength = selectedLength;
          } else {
            selectedLength = null;
            lastSelectedLength = null;
          }
        }
        
        lengthBtns.forEach(btn => {
          const type = btn.dataset.lengthType || btn.dataset.value.toLowerCase();
          const exists = existingLengths.has(type);
          if (!exists) {
            btn.style.display = 'none';
            btn.disabled = true;
          } else {
            btn.style.display = '';
            btn.disabled = false;
          }

          const val = btn.dataset.value;
          if (selectedLength && val === selectedLength) {
            btn.classList.add('active');
          } else {
            btn.classList.remove('active');
          }
        });
        
        sizeBtns.forEach(btn => {
          if (selectedSize && btn.dataset.value === selectedSize) {
            btn.classList.add('active');
          } else {
            btn.classList.remove('active');
          }
        });
        
        dialog.showModal();
        document.documentElement.classList.add('sd-scroll-lock');
        updateSizeStates();
      };
      
      const updateSizeStates = () => {
        const hasFakeLength = lengthIndex === -1 && lengthBtns.length > 0;

        sizeBtns.forEach(btn => {
          const val = btn.dataset.value;
          
          if (hasFakeLength) {
            const sizeLengthType = getLengthTypeForSize(val);
            const filterLength = selectedLength || lastSelectedLength;
            if (filterLength && sizeLengthType !== filterLength) {
              btn.style.display = 'none';
              btn.disabled = true;
              btn.classList.remove('active');
              if (selectedSize === val) selectedSize = null;
              return;
            } else {
              btn.style.display = '';
            }
          }

          const match = variants.find(v => {
            const matchesSize = v.options[sizeIndex] === val;
            const filterLength = selectedLength || lastSelectedLength;
            const matchesLength = lengthIndex === -1 || filterLength === null || v.options[lengthIndex] === filterLength;
            return matchesSize && matchesLength;
          });
          
          btn.disabled = !match || !match.available;
          if (btn.disabled) {
            btn.classList.remove('active');
            if (selectedSize === val) selectedSize = null;
          }
        });
        
        const isLengthRequired = lengthIndex !== -1 || hasFakeLength;
        const selectionExists = !!this.selections[productId];

        if (!selectedSize) {
          if (selectionExists) {
            submitBtn.textContent = submitBtn.dataset.removeText || 'Remove';
            submitBtn.disabled = false;
          } else {
            submitBtn.textContent = submitBtn.dataset.defaultText || 'Select size';
            submitBtn.disabled = true;
          }
        } else {
          submitBtn.textContent = submitBtn.dataset.defaultText || 'Select size';
          submitBtn.disabled = isLengthRequired && !selectedLength;
        }
      };
      
      trigger.addEventListener('click', openDrawer);
      closeBtn.addEventListener('click', closeDrawer);
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) closeDrawer();
      });
      
      const sizeGuideBtn = itemEl.querySelector('.size-guide-drawer__trigger-open');
      if (sizeGuideBtn) {
        sizeGuideBtn.addEventListener('click', () => {
          closeDrawer();
        });
      }
      
      const enquireLinks = itemEl.querySelectorAll('a.enquire-link[href="#enquire"]');
      enquireLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          dialog.close();
          document.documentElement.classList.remove('sd-scroll-lock');
          
          const storeAvailability = itemEl.querySelector('.suit-store-availability');
          if (storeAvailability) {
            if (typeof storeAvailability.open === 'function') {
              storeAvailability.open();
            } else {
              const saDrawer = storeAvailability.querySelector('.sa-drawer');
              if (saDrawer) {
                saDrawer.setAttribute('open', '');
                document.documentElement.classList.add('sa-scroll-lock');
              }
            }
          }
        });
      });
      
      lengthBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const wasActive = btn.classList.contains('active');
          lengthBtns.forEach(b => b.classList.remove('active'));
          if (wasActive) {
            selectedLength = null;
          } else {
            btn.classList.add('active');
            selectedLength = btn.dataset.value;
            lastSelectedLength = btn.dataset.value;
          }
          updateSizeStates();
        });
      });
      
      sizeBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const wasActive = btn.classList.contains('active');
          sizeBtns.forEach(b => b.classList.remove('active'));
          if (wasActive) {
            selectedSize = null;
          } else {
            btn.classList.add('active');
            selectedSize = btn.dataset.value;
          }
          updateSizeStates();
        });
      });
      
      submitBtn.addEventListener('click', () => {
        if (!selectedSize) {
          delete this.selections[productId];
          
          const defaultText = trigger.dataset.defaultText || 'Select size';
          trigger.querySelector('.select-trigger__text').textContent = defaultText;
          trigger.querySelector('.select-trigger__icon--plus').classList.remove('hidden');
          trigger.querySelector('.select-trigger__icon--check').classList.add('hidden');
          trigger.classList.remove('has-selected-size');
          
          this.updateTotals();
          closeDrawer();
          return;
        }

        const match = variants.find(v => {
          const matchesSize = v.options[sizeIndex] === selectedSize;
          const matchesLength = lengthIndex === -1 || v.options[lengthIndex] === selectedLength;
          return matchesSize && matchesLength;
        });
        
        if (match) {
          let displayTitle = selectedSize;
          if (lengthIndex === -1 && lengthBtns.length > 0) {
            let cleanSize = selectedSize;
            const lastChar = selectedSize.slice(-1).toLowerCase();
            if (selectedSize.toLowerCase() !== 'xs' && selectedSize.toLowerCase() !== 's' && (lastChar === 's' || lastChar === 'r' || lastChar === 't')) {
              cleanSize = selectedSize.slice(0, -1);
            }
            displayTitle = cleanSize + (selectedLength ? ' ' + selectedLength.charAt(0).toUpperCase() + selectedLength.slice(1) : '');
          } else {
            displayTitle = selectedSize + (selectedLength ? ' ' + selectedLength : '');
          }

          this.selections[productId] = {
            variantId: match.id,
            title: displayTitle,
            price: match.price,
            size: selectedSize,
            length: selectedLength
          };
          
          trigger.querySelector('.select-trigger__text').textContent = displayTitle;
          trigger.querySelector('.select-trigger__icon--plus').classList.add('hidden');
          trigger.querySelector('.select-trigger__icon--check').classList.remove('hidden');
          trigger.classList.add('has-selected-size');
          
          this.updateTotals();
          closeDrawer();
        }
      });
    });
    
    // Listen to variant changes on the main product to update the base price and re-apply override
    const closestSection = this.closest('.shopify-section');
    if (closestSection) {
      closestSection.addEventListener('variant:update', (event) => {
        this.overrideMainForm();
        if (event.detail.resource) {
          this.mainProductPrice = event.detail.resource.price;
          this.updateTotals();
        }
      });
    }
    
    this.overrideMainForm();
    this.updateTotals();
  }
  
  overrideMainForm() {
    const productFormComponent = document.querySelector('product-form-component');
    if (productFormComponent) {
      // Check if productFormComponent is for our main product ID
      if (productFormComponent.dataset.productId !== this.mainProductId) return;
      
      productFormComponent.handleSubmit = async (event) => {
        event.preventDefault();
        
        const selectedItems = Object.values(this.selections);
        
        if (selectedItems.length === 0) {
          alert('Please select a size for at least one item.');
          return;
        }
        
        const submitBtn = productFormComponent.querySelector('[type="submit"]') || productFormComponent.querySelector('.button');
        const originalText = submitBtn ? submitBtn.textContent : '';
        
        if (submitBtn) {
          submitBtn.disabled = true;
          const textEl = submitBtn.querySelector('.add-to-cart-text') || submitBtn;
          // textEl.textContent = 'Adding to cart...';
        }
        
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const cartItemComponentsSectionIds = [];
        for (const item of cartItemsComponents) {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            cartItemComponentsSectionIds.push(item.dataset.sectionId);
          }
        }
        
        const payload = {
          items: selectedItems.map(item => ({
            id: Number(item.variantId),
            quantity: 1
          })),
          sections: cartItemComponentsSectionIds.join(',')
        };
        
        try {
          const response = await fetch((window.Theme?.routes?.cart_add_url || '/cart/add.js'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
          });
          
          const cart = await response.json();
          
          if (cart.status) {
            alert(cart.message + ': ' + cart.description);
          } else {
            const totalQuantity = selectedItems.length;
            document.dispatchEvent(
              new CartAddEvent(cart, selectedItems[0].variantId.toString(), {
                source: 'product-form-component',
                itemCount: totalQuantity,
                productId: null,
                sections: cart.sections
              })
            );
          }
        } catch (err) {
          console.error('Error adding suit items to cart:', err);
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            const textEl = submitBtn.querySelector('.add-to-cart-text') || submitBtn;
            textEl.textContent = originalText;
          }
        }
      };
    }
  }
  
  updateTotals() {
    const selectedItems = Object.values(this.selections);
    const totalCents = selectedItems.reduce((sum, item) => sum + item.price, 0);
    const count = selectedItems.length;
    
    this.totalPriceEl.textContent = formatMoney(totalCents, this.moneyFormat, this.currency);
    this.statusEl.textContent = `${count} item${count === 1 ? '' : 's'} selected`;
    
    if (this.totalRowEl) {
      if (count === 0) {
        this.totalRowEl.style.display = 'none';
      } else {
        this.totalRowEl.style.display = '';
      }
    }

    this.updateMainSubmitButton();
  }

  updateMainSubmitButton() {
    const productFormComponent = document.querySelector('product-form-component');
    if (!productFormComponent) return;
    if (productFormComponent.dataset.productId !== this.mainProductId) return;

    const submitBtn = productFormComponent.querySelector('[type="submit"]') || productFormComponent.querySelector('.button');
    if (!submitBtn) return;

    const textEl = submitBtn.querySelector('.add-to-cart-text__content span span') || submitBtn.querySelector('.add-to-cart-text') || submitBtn;
    const selectedItems = Object.values(this.selections);
    const count = selectedItems.length;

    if (count === 0) {
      textEl.textContent = 'Please select sizing';
      submitBtn.style.opacity = '0.5';
      submitBtn.disabled = true;
    } else {
      textEl.textContent = this.defaultAddToCartText;
      submitBtn.style.opacity = '';
      submitBtn.disabled = false;
    }
  }
}

if (!customElements.get('suit-set')) {
  customElements.define('suit-set', SuitSet);
}
